import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/storage";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import Bugsnag from "@bugsnag/js";

import organizationIdSelector from "../atoms/organizationIdSelector";
import { nanoid } from "nanoid";
import { consola } from "consola";

export type useClientLogDataProps = {
  clientId: string;
  onSnapshot: (
    snapshot: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>,
  ) => void;
};

export type LogData = {
  id?: string;
  body: string;
  createdOn: Date;
  updatedOn: Date;
};

const useClientLogData = ({ clientId, onSnapshot }: useClientLogDataProps) => {
  const organizationId = getRecoil(organizationIdSelector);

  async function editLogData(logData: LogData) {
    // Add the log data here.
    let id = nanoid();
    if (logData.id) {
      id = logData.id;
    }
    const clientLogRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("logs")
      .doc(id);

    try {
      consola.debug("+++++ WRITE => useClientLogData: editLogData")
      await clientLogRef.set(
        {
          id: id,
          body: logData.body,
          createdOn: logData.createdOn,
          updatedOn: logData.updatedOn,
        },
        { merge: true },
      );
    } catch (e) {
      Bugsnag.notify(new Error("Error saving log data"));
    }
  }

  async function deleteLogData(id: string) {
    const clientLogRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("logs")
      .doc(id);

    try {
      await clientLogRef.delete();
    } catch (e) {
      Bugsnag.notify(e as Error);
    }
  }

  React.useEffect(() => {
    const clientRef = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("logs")
      .orderBy("createdOn", "desc");

    const subscription = clientRef.onSnapshot((snapshot) => {
      consola.debug("++++++ SNAPSHOT -> useClientLogData")
      if (onSnapshot) {
        onSnapshot(snapshot);
      }
    });

    return () => {
      subscription();
    };
  }, [clientId, organizationId, onSnapshot]);

  return { editLogData, deleteLogData };
};

export default useClientLogData;
